/**
 * ActivityFrame (#1089 portrait, #1091 landscape) — the single
 * standardized scaffold every in-call ACTIVITY renders inside. It owns
 * ALL of the call chrome so the activity components (DominoGame,
 * SolitaireGame, GameCarousel, VideoOverlay, MusicView, PhotoCarousel)
 * are pure content with no embedded CallStrip, close, or hangup.
 *
 * Four fixed regions, two arrangements — chosen from the frame's OWN
 * measured dimensions (NOT a viewport-mode bucket, so a phone rotated
 * to landscape gets the side-by-side layout the moment its width
 * exceeds its height + the 650px floor):
 *
 *   Stacked (frame is portrait OR too narrow for a chrome+content
 *   split) — a three-row column:
 *     row 1  state icon + label  ........................  [X]
 *     row 2  transcription (current-speaker; collapses to 0 when empty)
 *     row 3  content (fills the rest; sized from its measured box)
 *
 *   Side-by-side (frame is wider than tall AND ≥650px wide) — two columns:
 *     col 1 (~80%)  content
 *     col 2 (~20%)  [X]            (top-right)
 *                   state icon     (compact)
 *                   state text
 *                   transcription  (largest area; blank when empty —
 *                                   no collapse, the column is fixed-width)
 *
 * Why 650, not the old `tablet-landscape` ≥850 viewport gate? The
 * chrome column has `minWidth: 132` + ~20px padding; the content
 * column wants room to breathe. At 650 the chrome lands ~140-150px
 * and content gets ~500px — comfortable on phones in landscape (~640-
 * 800w on most modern devices) as well as tablets. Below 650 we stay
 * stacked, matching the pre-rotation portrait look.
 *
 * Locked design (#1089):
 *   - the X closes the activity and returns to the conversation view
 *     (the call stays connected — NOT a hangup);
 *   - there is NO hangup affordance inside an activity;
 *   - the state vocabulary (icon + label) is the SAME as the in-call
 *     CallStrip (imported, rule 9);
 *   - the transcription reuses the #1032 current-speaker model
 *     (resolveFocalPanel) — Berta's reply, or the user's live transcript
 *     on their turn — and collapses when there is nothing to show;
 *   - row-3 / content-column content sizes itself from its OWN measured
 *     box (ResizeObserver), exposed via useActivityBox(), so it is
 *     optimal on phone, PC, and tablet without viewport-bucket math.
 */
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useLang } from '../i18n/LanguageContext';
import { resolveFocalPanel } from './BertaReplyPanel';
import { STATUS_EMOJI, STATUS_LABEL } from './CallStrip';

// Width floor at which the side-by-side layout fits without crushing
// the chrome column. See the file-header comment.
export const FRAME_SPLIT_MIN_WIDTH = 650;

const CREAM = '#fff8f0';
const INK = '#2C2C2C';
const INK_SOFT = '#6b5d50';
const BRAND_ORANGE = '#E8845A';

// Measured content-box {width,height} in CSS px, published to the active
// activity so it can size its own content (games read this for tile/card
// dimensions instead of window.innerWidth). 0×0 until the first measure.
const ActivityBoxContext = createContext({ width: 0, height: 0 });
export function useActivityBox() {
  return useContext(ActivityBoxContext);
}

function _useTabletStrings() {
  const ctx = useLang();
  return ctx && ctx.t && ctx.t.tablet ? ctx.t.tablet : null;
}

export default function ActivityFrame({
  status = 'idle',
  reply = '',
  transcript = '',
  // #1032 — true on the user's turn; flips the transcription region to
  // their live words under a "You" heading.
  showingUser = false,
  companionName = 'Berta',
  // X handler — closes the activity, returns to the conversation view.
  onClose = () => {},
  children,
}) {
  // Measure the FRAME itself (not the content slot — that's `useActivityBox`
  // below, exposed to children). Decision is taken on the frame's actual
  // rendered dimensions, so a phone rotating to landscape immediately
  // switches to the side-by-side layout the moment ResizeObserver fires —
  // no viewport-mode bucket gate.
  const frameRef = useRef(null);
  const [frameBox, setFrameBox] = useState(() => ({
    // Seed from `window` so the first paint matches the final state on
    // a full-screen scaffold (the frame is position:fixed; inset:0).
    // Avoids the 0×0 → real-dim flash; ResizeObserver corrects any
    // subsequent change (rotation, chrome appearing, etc.).
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0,
  }));
  useEffect(() => {
    const el = frameRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver((entries) => {
      const r = entries[0] && entries[0].contentRect;
      if (r) setFrameBox({ width: Math.round(r.width), height: Math.round(r.height) });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const isLandscape =
    frameBox.width > frameBox.height && frameBox.width >= FRAME_SPLIT_MIN_WIDTH;
  const t = _useTabletStrings();

  // ── State row (same vocabulary as CallStrip) ──────────────────────────
  const statusLabel =
    (t && t.callstrip && t.callstrip.status && t.callstrip.status[status]) ||
    STATUS_LABEL[status] || '';
  const statusIcon = STATUS_EMOJI[status] || '';

  // ── Transcription (current-speaker, #1032) — collapses when empty ────
  const youLabel = (t && t.bertareply && t.bertareply.you) || 'You';
  const { heading, body } = resolveFocalPanel({
    reply,
    userTranscript: transcript,
    showUser: showingUser,
    companionName,
    youLabel,
    placeholder: '',
    // Activities never show the idle "Hello…" placeholder — an empty
    // current-speaker region collapses instead.
    allowPlaceholder: false,
  });
  const hasTranscript = !!(body && body.trim());

  const closeAria =
    (t && t.activity && t.activity.closeAria) || 'Close';

  // ── Measured content box → useActivityBox() ──────────────────────────
  const contentRef = useRef(null);
  const [box, setBox] = useState({ width: 0, height: 0 });
  useEffect(() => {
    const el = contentRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver((entries) => {
      const r = entries[0] && entries[0].contentRect;
      if (r) setBox({ width: Math.round(r.width), height: Math.round(r.height) });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const closeButton = (
    <button onClick={onClose} aria-label={closeAria} style={styles.closeBtn}>
      ✕
    </button>
  );

  const transcriptionBlock = (
    <div style={styles.transcription}>
      <div style={styles.transcriptionHeading}>{heading}</div>
      <div style={styles.transcriptionBody}>{body}</div>
    </div>
  );

  const contentSlot = (
    <div ref={contentRef} style={styles.content}>
      <ActivityBoxContext.Provider value={box}>
        {children}
      </ActivityBoxContext.Provider>
    </div>
  );

  if (isLandscape) {
    return (
      <div ref={frameRef} style={styles.frameLandscape}>
        <div style={styles.contentColLandscape}>{contentSlot}</div>
        <div style={styles.chromeColLandscape}>
          <div style={styles.chromeCloseRow}>{closeButton}</div>
          <div style={styles.chromeIcon} aria-hidden="true">{statusIcon}</div>
          <div style={styles.chromeStateText}>{statusLabel}</div>
          <div style={styles.chromeTranscriptionWrap}>
            {hasTranscript && transcriptionBlock}
          </div>
        </div>
      </div>
    );
  }

  // Portrait / stacked.
  return (
    <div ref={frameRef} style={styles.framePortrait}>
      <div style={styles.stateRow}>
        <div style={styles.stateInline}>
          <span style={styles.stateInlineIcon} aria-hidden="true">{statusIcon}</span>
          <span style={styles.stateInlineLabel}>{statusLabel}</span>
        </div>
        {closeButton}
      </div>
      {hasTranscript && (
        <div style={styles.transcriptionRowPortrait}>{transcriptionBlock}</div>
      )}
      {contentSlot}
    </div>
  );
}

const styles = {
  // Full-screen scaffold; sits over the connected-call layout (z 100),
  // same plane the activity overlays used before #1089.
  framePortrait: {
    position: 'fixed',
    inset: 0,
    zIndex: 9998,
    background: CREAM,
    display: 'flex',
    flexDirection: 'column',
    paddingTop: 'env(safe-area-inset-top)',
    boxSizing: 'border-box',
  },
  frameLandscape: {
    position: 'fixed',
    inset: 0,
    zIndex: 9998,
    background: CREAM,
    display: 'flex',
    flexDirection: 'row',
    paddingTop: 'env(safe-area-inset-top)',
    boxSizing: 'border-box',
  },

  // Portrait row 1 — state (left) + close (right).
  stateRow: {
    flex: '0 0 auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 12px',
    gap: 12,
  },
  stateInline: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    minWidth: 0,
  },
  stateInlineIcon: { fontSize: 22, lineHeight: 1 },
  stateInlineLabel: {
    fontSize: 'clamp(15px, 2.2vh, 19px)',
    fontWeight: 600,
    color: INK,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },

  // Portrait row 2 — transcription, capped height, scrolls; the row is
  // only rendered when there's something to show (collapse-when-empty).
  transcriptionRowPortrait: {
    flex: '0 0 auto',
    maxHeight: 'clamp(64px, 14vh, 120px)',
    overflow: 'hidden',
    padding: '0 12px 8px',
    display: 'flex',
  },

  // Landscape — content column (~80%) + chrome column (~20%).
  contentColLandscape: {
    flex: '1 1 78%',
    minWidth: 0,
    minHeight: 0,
    display: 'flex',
  },
  chromeColLandscape: {
    flex: '1 1 22%',
    minWidth: 132,
    maxWidth: 280,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    padding: '8px 10px',
    borderLeft: '1px solid rgba(232,132,90,0.18)',
    boxSizing: 'border-box',
  },
  chromeCloseRow: {
    flex: '0 0 auto',
    display: 'flex',
    justifyContent: 'flex-end',
  },
  chromeIcon: {
    flex: '0 0 auto',
    fontSize: 26,
    lineHeight: 1,
    textAlign: 'center',
  },
  chromeStateText: {
    flex: '0 0 auto',
    fontSize: 'clamp(14px, 1.7vw, 18px)',
    fontWeight: 600,
    color: INK,
    textAlign: 'center',
  },
  // Largest region of the chrome column.
  chromeTranscriptionWrap: {
    flex: '1 1 auto',
    minHeight: 0,
    overflow: 'hidden',
    display: 'flex',
  },

  // Shared transcription card (used in both arrangements).
  transcription: {
    width: '100%',
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    background: 'rgba(255,255,255,0.6)',
    border: '1px solid rgba(232,132,90,0.18)',
    borderRadius: 16,
    padding: '8px 12px',
    boxSizing: 'border-box',
    overflow: 'hidden',
  },
  transcriptionHeading: {
    flex: '0 0 auto',
    fontSize: '0.72rem',
    fontWeight: 700,
    color: BRAND_ORANGE,
    letterSpacing: '0.07em',
    textTransform: 'uppercase',
    marginBottom: 4,
  },
  transcriptionBody: {
    flex: '1 1 auto',
    minHeight: 0,
    overflowY: 'auto',
    overflowX: 'hidden',
    overscrollBehavior: 'contain',
    fontSize: 'clamp(16px, 2vw, 22px)',
    lineHeight: 1.4,
    color: INK,
    fontWeight: 500,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    WebkitOverflowScrolling: 'touch',
  },

  // Content slot — the measured box. Fills its region; the activity
  // content paints its own background (felt / black TV frame / etc.).
  content: {
    flex: '1 1 auto',
    minWidth: 0,
    minHeight: 0,
    position: 'relative',
    display: 'flex',
    overflow: 'hidden',
  },

  // Close (X) — reachable but not so prominent it's tapped by accident
  // (cf. the #1058 re-dial caution). One affordance, owned by the frame.
  closeBtn: {
    flex: '0 0 auto',
    width: 46,
    height: 46,
    borderRadius: '50%',
    border: '1px solid rgba(44,44,44,0.18)',
    background: 'rgba(255,255,255,0.85)',
    color: INK_SOFT,
    fontSize: 22,
    lineHeight: 1,
    cursor: 'pointer',
    display: 'grid',
    placeItems: 'center',
  },
};
